import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

type Props = {
  isOpen: boolean;
  initialNotes: string;
  title?: string;
  onSave: (notes: string) => void;
  onClose: () => void;
  isSaving?: boolean;
};

export function NotesModal({ isOpen, initialNotes, title = 'Notes', onSave, onClose, isSaving }: Props) {
  const [value, setValue] = useState(initialNotes);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setValue(initialNotes);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [isOpen, initialNotes]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="notes-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-candy-line bg-candy-white p-5 shadow-2 dark:border-candy-dark-line dark:bg-candy-dark-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 id="notes-modal-title" className="text-base font-semibold text-candy-ink dark:text-candy-dark-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex items-center justify-center rounded-md p-1 text-candy-ink-muted hover:bg-candy-cream dark:text-candy-dark-ink-muted dark:hover:bg-candy-dark-surface-warm"
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          placeholder="Add a note…"
          className="w-full resize-none rounded-md border border-candy-line bg-candy-cream px-3 py-2 text-sm text-candy-ink placeholder:text-candy-ink-muted focus:border-candy-pink focus:outline-none dark:border-candy-dark-line dark:bg-candy-dark-bg dark:text-candy-dark-ink dark:placeholder:text-candy-dark-ink-muted dark:focus:border-candy-pink-dark"
        />
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm text-candy-ink-muted hover:text-candy-ink dark:text-candy-dark-ink-muted dark:hover:text-candy-dark-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(value)}
            disabled={isSaving}
            className="rounded-md bg-candy-pink px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-60 dark:bg-candy-pink-dark dark:text-candy-dark-bg"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
